// src/components/layout/DashboardLayout.tsx
import React, { Suspense, startTransition, useState, useCallback } from "react";
import { MainTabBar, LoadingSpinner } from "@/components/common";
import type { IconName } from "lucide-react/dynamic";
import { formatDate } from "@/utils/formatters";

interface Tab {
  id: string;
  label: string;
  icon: IconName;
  component: React.ReactNode;
}

interface DashboardLayoutProps {
  /** Page title (optional - header hidden if not provided) */
  title?: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Last updated timestamp */
  lastUpdated?: Date | string | null;
  /** Tab definitions with components */
  tabs: Tab[];
  /** Default tab to show */
  defaultTabId?: string;
  /** Optional header content (e.g., category buttons) */
  headerContent?: React.ReactNode;
  /** Error state */
  error?: Error | null;
  /** Retry callback for error state */
  onRetry?: () => void;
  /** Whether to use Suspense for tab content (default: true) */
  useSuspense?: boolean;
  /** Custom class for the container */
  className?: string;
  /** When true, removes outer padding (for embedding in another layout) */
  embedded?: boolean;
}

/**
 * Unified dashboard layout component that provides consistent structure
 * across all dashboard pages.
 * 
 * Features:
 * - Consistent header with title, subtitle, and last updated
 * - Tab navigation with smooth transitions using startTransition
 * - Loading states during tab switches
 * - Error handling with retry
 * - Optional Suspense support for lazy-loaded tab content
 * 
 * @example
 * <DashboardLayout
 *   title="Sector Comparison"
 *   subtitle="Compare sectors across performance, liquidity, derivatives"
 *   lastUpdated={lastUpdated}
 *   tabs={[
 *     { id: 'overview', label: 'Overview', icon: 'bar-chart-3', component: <OverviewView /> },
 *     { id: 'performance', label: 'Performance', icon: 'trending-up', component: <PerformanceView /> },
 *   ]}
 *   defaultTabId="overview"
 *   error={isError ? error : null}
 *   onRetry={refetch}
 * />
 */
export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  title,
  subtitle,
  lastUpdated,
  tabs,
  defaultTabId,
  headerContent,
  error,
  onRetry,
  useSuspense = true,
  className = "",
  embedded = false,
}) => {
  const [activeTabUI, setActiveTabUI] = useState(defaultTabId || tabs[0]?.id || "");
  const [activeTabContent, setActiveTabContent] = useState(activeTabUI);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const switchTab = useCallback((key: string) => {
    if (key === activeTabUI) return;
    
    setActiveTabUI(key);
    setIsTransitioning(true);

    // Small delay to allow UI to update before heavy unmount
    setTimeout(() => {
      startTransition(() => {
        setActiveTabContent(key);
        // Allow charts to unmount before removing loading state
        setTimeout(() => setIsTransitioning(false), 100);
      });
    }, 50);
  }, [activeTabUI]);

  if (error) {
    return (
      <div className="w-full max-w-[3000px] mx-auto px-2 sm:px-4">
        <div className="alert alert-error flex items-center justify-between">
          <span>Error loading dashboard data: {error.message}</span>
          {onRetry && (
            <button className="btn btn-sm btn-outline" onClick={onRetry}>
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  const activeTab = tabs.find((t) => t.id === activeTabContent);

  const renderContent = () => {
    if (isTransitioning) {
      return (
        <div className="flex justify-center items-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    if (useSuspense) {
      return (
        <Suspense
          fallback={
            <div className="flex justify-center items-center min-h-[400px]">
              <LoadingSpinner size="lg" />
            </div>
          }
        >
          {activeTab?.component}
        </Suspense>
      );
    }

    return activeTab?.component;
  };

  return (
    <div className={`w-full ${className}`}>
      <div className={embedded ? "" : "space-y-6"}>
        {/* Header */}
        {(title || subtitle || lastUpdated) && (
          <div>
            {title && (
              <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">{title}</h1>
            )}
            {subtitle && (
              <p className="text-sm text-gray-300 mb-1">{subtitle}</p>
            )}
            {lastUpdated && (
              <div className="flex items-center text-xs text-gray-400">
                <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" />
                Last updated: {typeof lastUpdated === "string" ? lastUpdated : formatDate(lastUpdated)}
              </div>
            )}
          </div>
        )}

        {/* Optional header content (category buttons, filters, etc.) */}
        {headerContent}

        {/* Tab navigation */}
        <MainTabBar
          tabs={tabs.map(({ id, label, icon }) => ({ id, label, icon }))}
          activeId={activeTabUI}
          onTabClick={switchTab}
        />

        {/* Tab content */}
        {renderContent()}
      </div>
    </div>
  );
};

export default DashboardLayout;

